import { createSlice, Slice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { UserRole } from 'network-utils';
import { Locale } from 'translations';

/* eslint-disable no-param-reassign */
type userSliceStateType = {
    loading: boolean;
    isPlatformAuthenticated: boolean;
    userId: number;
    userUuid: string;
    userNickname: string;
    userFullName: string;
    role: keyof typeof UserRole;
    locale: keyof typeof Locale;
    token: string;
    tenantName: string;
    customerDisplayName: string;
    isLeanUser: boolean;
    certificateLinkedinShareEnabled: boolean;
    isPLG: boolean;
    customBrandName: string | null;
    isCxUniversity: boolean;
    contentManagement: boolean;
    isTournamentsGamePackageEnabled: boolean;
    labsAccess: boolean;
};

const initialState: userSliceStateType = {
    loading: true,
    isPlatformAuthenticated: false,
    userId: 1,
    userUuid: '',
    role: 'user',
    locale: 'en',
    token: '',
    customerDisplayName: '',
    tenantName: '',
    isLeanUser: false,
    userNickname: '',
    userFullName: '',
    certificateLinkedinShareEnabled: false,
    isPLG: false,
    customBrandName: null,
    isCxUniversity: false,
    contentManagement: false,
    isTournamentsGamePackageEnabled: false,
    labsAccess: false
};

export const userSlice: Slice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>): void => {
            state.loading = action.payload;
        },
        setIsPlatformAuthenticated: (state, action: PayloadAction<boolean>): void => {
            state.isPlatformAuthenticated = action.payload;
        },
        setUserId: (state, action: PayloadAction<number>): void => {
            state.userId = action.payload;
        },
        setRole: (state, action: PayloadAction<keyof typeof UserRole>): void => {
            state.role = action.payload;
        },
        setLocale: (state, action: PayloadAction<keyof typeof Locale>): void => {
            moment.locale(action.payload);
            state.locale = action.payload;
        },
        setUserUuid: (state, action: PayloadAction<string>): void => {
            state.userUuid = action.payload;
        },
        setToken: (state, action: PayloadAction<string>): void => {
            state.token = action.payload;
        },
        setCustomerDisplayName: (state, action: PayloadAction<string>): void => {
            state.customerDisplayName = action.payload;
        },
        setIsLeanUser: (state, action: PayloadAction<boolean>): void => {
            state.isLeanUser = action.payload;
        },
        setUserNickname: (state, action: PayloadAction<string>): void => {
            state.userNickname = action.payload;
        },
        setUserFullName: (state, action: PayloadAction<string>): void => {
            state.userFullName = action.payload;
        },
        setCertificateLinkedinShareEnabled: (state, action: PayloadAction<boolean>): void => {
            state.certificateLinkedinShareEnabled = action.payload;
        },
        setTenantName: (state, action: PayloadAction<boolean>): void => {
            state.tenantName = action.payload;
        },
        setIsPLG: (state, action: PayloadAction<boolean>): void => {
            state.isPLG = action.payload;
        },
        setCustomBrandName: (state, action: PayloadAction<boolean>): void => {
            state.customBrandName = action.payload;
        },
        setIsCxUniversity: (state, action: PayloadAction<boolean>): void => {
            state.isCxUniversity = action.payload;
        },
        setContentManagement: (state, action: PayloadAction<boolean>): void => {
            state.contentManagement = action.payload;
        },
        setIsTournamentPackageEnabled: (state, action: PayloadAction<boolean>): void => {
            state.isTournamentsGamePackageEnabled = action.payload;
        },
        setLabsAccess: (state, action: PayloadAction<boolean>): void => {
            state.labsAccess = action.payload;
        }
    }
});

export const {
    setLoading,
    setIsPlatformAuthenticated,
    setIsTournamentsAuthenticated,
    setUserId,
    setRole,
    setLocale,
    setUserUuid,
    setToken,
    setCustomerDisplayName,
    setIsLeanUser,
    setUserNickname,
    setUserFullName,
    setCertificateLinkedinShareEnabled,
    setTenantName,
    setIsPLG,
    setCustomBrandName,
    setIsCxUniversity,
    setContentManagement,
    setIsTournamentPackageEnabled,
    setLabsAccess,
    setIsAutoUserMessageEnabled
} = userSlice.actions;

type userSliceState = { user: userSliceStateType };
export const selectLoading = (state: userSliceState): boolean => state.user.loading;
export const selectIsPlatformAuthenticated = (state: userSliceState): boolean => state.user.isPlatformAuthenticated;
export const selectUserId = (state: userSliceState): number => state.user.userId;
export const selectRole = (state: userSliceState): keyof typeof UserRole => state.user.role;
export const selectLocale = (state: userSliceState): keyof typeof Locale => state.user.locale;
export const selectUserUuid = (state: userSliceState): string => state.user.userUuid;
export const selectToken = (state: userSliceState): string => state.user.token;
export const selectCustomerDisplayName = (state: userSliceState): string => state.user.customerDisplayName;
export const selectIsLeanUser = (state: userSliceState): boolean => state.user.isLeanUser;
export const selectUserNickname = (state: userSliceState): string => state.user.userNickname;
export const selectUserFullName = (state: userSliceState): string => state.user.userFullName;
export const selectCertificateLinkedinShareEnabled = (state: userSliceState): boolean =>
    state.user.certificateLinkedinShareEnabled;
export const selectTenantName = (state: userSliceState): string => state.user.tenantName;
export const selectIsPLG = (state: userSliceState): boolean => state.user.isPLG;
export const selectCustomBrandName = (state: userSliceState): string | null => state.user.customBrandName;
export const selectIsCxUniversity = (state: userSliceState): boolean => state.user.isCxUniversity;
export const selectContentManagement = (state: userSliceState): boolean => state.user.contentManagement;
export const selectIsTournamentsGamePackageEnabled = (state: userSliceState): boolean =>
    state.user.isTournamentsGamePackageEnabled;
export const selectLabsAccess = (state: userSliceState): boolean => state.user.labsAccess;

export default userSlice.reducer;
